import { ListResult } from '../fetchers/ListResult';
import { PermissionsHelper } from '../helpers/PermissionsHelper';
import { PermissionsMerger } from '../helpers/PermissionsMerger';
import { ResultHelper } from '../helpers/ResultHelper';
import { IGranteePermissions } from '../models/IGranteePermissions';
import { Module } from '../Module';
import { IGetExplicitPermissionsResult } from '../ServerApi/requests/GetExplicitPermissionsRequest';
import { IResult } from '../ServerApi/types/IResponse';
import { ITypedIdWithName } from '../ServerApi/types/ITypedId';
import { ITypedIds } from '../ServerApi/types/ITypedIds';
import { ServerService } from '../services/ServerService';

/** This is a wrapper around getExplicitPermissions request
  * IGetExplicitPermissionsResult - what REST api returns
  * ExplicitPermissionsResult - what service returns. 
  * UI uses ExplicitPermissionsResult
  * */
export interface ExplicitPermissionsResult extends IResult {
  permissionsList: ListResult<IGranteePermissions>;
  sourceAuthorizablesById: _.Dictionary<ITypedIdWithName>;
}

export class ExplicitPermissions {

  public static $inject = ['ServerService'];

  constructor(private server: ServerService) {
  }

  public forAuthorizables(authorizables: ITypedIds): ng.IPromise<ExplicitPermissionsResult> {

    const requests = PermissionsHelper.explicitPermissionsRequestsForAuthorizables(authorizables);
    const combineResultsFn = (results: IGetExplicitPermissionsResult[]) => {
      return {
        permissions: ResultHelper.combine(results, (result) => result.permissions),
        groups: ResultHelper.combine(results, (result) => result.groups),
        users: ResultHelper.combine(results, (result) => result.users),
        sourceAuthorizableById: ResultHelper.combineByKey(results, (result) => result.sourceAuthorizableById),
        errors: ResultHelper.combineErrors(results)
      };
    };

    return this.server.sendRequestsParallel(requests, combineResultsFn)
      .then<ExplicitPermissionsResult>((result: IGetExplicitPermissionsResult) => {
        const list = PermissionsMerger.makeGranteePermissionsList(
          result.permissions, authorizables.ids, result.groups, result.users);
        return {
          permissionsList: new ListResult<IGranteePermissions>(list),
          sourceAuthorizablesById: result.sourceAuthorizableById,
          errors: result.errors
        };
    });
  }

  public setForAuthorizables(authorizables: ITypedIds, granteePermissions: IGranteePermissions): ng.IPromise<IResult> {
    const requests = PermissionsHelper.setExplicitPermissionsRequestsForAuthorizables(authorizables, granteePermissions);
    return this.server.sendRequestsParallel(requests, ResultHelper.combineResults);
  }

}

Module.instance().service('ExplicitPermissions', ExplicitPermissions);
